<template>
  <form @submit.prevent>
    <div>
      <h2>
        <span v-if="isEditMode">"{{ editedOrganization.name }}" {{ $t("common.update") }}</span>
        <span v-else>{{ $t("organization.new") }}</span>
      </h2>
      <form class="needs-validation" @submit.prevent="save">
        <div role="tablist">
          <b-card no-body class="mb-1 shadow-none">
            <b-card-header header-tag="header" role="tab">
              <a v-b-toggle="'accordion_organization'" class="text-dark" href="javascript: void(0);">
                <div class="row">
                  <div class="col-md-6">
                    <h5>
                      <i class="bx bx-sitemap"></i>
                      {{ $t("organization.information") }}
                    </h5>
                  </div>
                  <div class="col-md-6 text-right">
                    <i v-if="submitted && !$v.form.$anyError" class="bx bx-check text-success h1"></i>
                    <i v-if="submitted && $v.form.$anyError" class="bx bx-x text-danger h1"></i>
                  </div>
                </div>
              </a>
            </b-card-header>
            <b-collapse id="accordion_organization" :visible="true" accordion="accordion" role="tabpanel">
              <b-card-body>
                <ImageCropper id="imageCropper" :src="form.logo" @cropperImage="cropperImage" />

                <div class="row">
                  <div
                    class="
                      col-md-3
                      d-flex
                      flex-column
                      justify-content-center
                      align-items-center
                    "
                  >
                    <a href="javascript: void(0);" @click="openCropper">
                      <img
                        class="
                          avatar-title
                          rounded-circle
                          bg-light
                          text-danger
                          font-size-16
                        "
                        :src="form.logo"
                        alt
                      />
                    </a>
                    <a class="m-1 text-info" href="javascript: void(0);" @click="openCropper"><i class="bx bx-edit-alt text-info"></i> {{ $t("common.update") }}</a>
                  </div>
                  <div class="col-md-9">
                    <div class="form-group">
                      <div class="mb-3">
                        <b-dropdown
                          v-if="!isEditMode"
                          split
                          :split-variant="workspace ? 'outline-primary' : 'outline-danger '"
                          :variant="workspace ? 'primary' : 'danger'"
                          block
                          menu-class="w-100"
                        >
                          <template v-slot:button-content>
                            <i class="mdi mdi-hexagon-multiple-outline mr-1"></i>
                            {{ workspace ? workspace.name : $t("organization.select-workspace-for-create") }}
                          </template>

                          <b-dropdown-text v-if="!workspaces" class="bg-light">
                            <empty width="150px">{{ $t("workspace.no-data") }}</empty>
                          </b-dropdown-text>

                          <b-dropdown-item
                            v-else
                            v-for="item in workspaces.docs"
                            :key="item._id"
                            @click="setWorkspace(item)"
                            :class="{ active: workspace && workspace._id === item._id }"
                            class="text-reset notification-item hider"
                          >
                            <i class="mdi mdi-hexagon-multiple-outline mr-1"></i>
                            <span class="align-middle">{{ item.name }}</span>
                          </b-dropdown-item>

                          <div class="d-flex justify-content-center my-3">
                            <ul class="pagination pagination-rounded mb-0">
                              <b-pagination
                                v-if="workspaces && workspaces.docs.length > 0"
                                v-model="request.options.page"
                                :total-rows="workspaces.totalDocs"
                                :per-page="20"
                                @click.native.stop
                                size="sm"
                              ></b-pagination>
                            </ul>
                          </div>
                        </b-dropdown>
                      </div>

                      <label class="mt-2">{{ $t("organization.name") }}</label>
                      <input
                        v-model="form.name"
                        class="form-control"
                        :placeholder="$t('organization.enter-name')"
                        :class="{
                          'is-invalid': submitted && $v.form.name.$error,
                          'is-valid': submitted && !$v.form.name.$error,
                        }"
                      />

                      <label class="mt-2">{{ $t("country.name") }}</label>
                      <filter-select
                        id="countriesFilter"
                        ref="countriesFilter"
                        :placeholder="$t('country.select')"
                        :multiple="false"
                        @filter="onCountriesSearch"
                        :selected="form.country"
                        @changed="onCountriesSelect"
                        track="code"
                      />
                    </div>
                  </div>
                </div>
                <div class="row"></div>
              </b-card-body>
            </b-collapse>
          </b-card>

          <b-card no-body class="mb-1 shadow-none">
            <b-card-header header-tag="header" role="tab">
              <a v-b-toggle="'accordion_company'" class="text-dark" href="javascript: void(0);">
                <div class="row">
                  <div class="col-md-6">
                    <h5>
                      <i class="bx bx-building-house"></i>
                      {{ $t("organization.company-details") }}
                    </h5>
                  </div>
                  <div class="col-md-6 text-right">
                    <i v-if="submitted && !$v.form.$anyError" class="bx bx-check text-success h1"></i>
                    <i v-if="submitted && $v.form.$anyError" class="bx bx-x text-danger h1"></i>
                  </div>
                </div>
              </a>
            </b-card-header>
            <b-collapse id="accordion_company" :visible="false" accordion="accordion" role="tabpanel">
              <b-card-body>
                <!-- -->
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.organization-nid") }}</label>
                    <input
                      v-model="form.nid"
                      @input="$v.form.nid.$touch()"
                      class="form-control"
                      :placeholder="$t('organization.organization-nid')"
                      :class="{
                        'is-invalid': submitted && $v.form.organization - nid.$error,
                        'is-valid': submitted && !$v.form.organization - nid.$error,
                      }"
                    />
                    <div v-if="submitted && $v.form.organization - nid.$error" class="invalid-feedback">
                      <span v-if="!$v.form.organization - nid.maxLength">
                        {{ $t("account.validation.vkn-tc-length") }}
                      </span>
                      <span v-if="!$v.form.organization - nid.minLength">
                        {{ $t("account.validation.vkn-tc-length") }}
                      </span>
                    </div>
                  </div>
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.organization-title") }}</label>
                    <input
                      v-model="form.title"
                      :placeholder="$t('organization.organization-title')"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.organization_title.$error,
                        'is-valid': submitted && !$v.form.organization_title.$error,
                      }"
                    />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.tax-office") }}</label>
                    <input
                      v-model="form.taxOffice"
                      :placeholder="$t('organization.tax-office')"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.tax_office.$error,
                        'is-valid': submitted && !$v.form.tax_office.$error,
                      }"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.web") }}</label>
                    <input
                      v-model="form.web"
                      :placeholder="$t('organization.web')"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.web.$error,
                        'is-valid': submitted && !$v.form.web.$error,
                      }"
                    />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.phone") }}</label>
                    <input
                      v-model="form.phone"
                      placeholder="+90 xxx xxx xx xx"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.phone.$error,
                        'is-valid': submitted && !$v.form.phone.$error,
                      }"
                    />
                    <div v-if="submitted && $v.form.phone.$error" class="invalid-feedback">
                      <span v-if="!$v.form.phone.maxLength">
                        {{ $t("validation.phone-max") }}
                      </span>
                      <span v-if="!$v.form.phone.minLength">
                        {{ $t("account.validation.phone-min") }}
                      </span>
                    </div>
                  </div>
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.email") }}</label>
                    <input
                      v-model="form.email"
                      type="email"
                      :placeholder="$t('organization.email')"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.email.$error,
                        'is-valid': submitted && !$v.form.email.$error,
                      }"
                    />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.address") }}</label>
                    <input
                      v-model="form.address"
                      :placeholder="$t('organization.address')"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.address.$error,
                        'is-valid': submitted && !$v.form.address.$error,
                      }"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.kep") }}</label>
                    <input
                      v-model="form.kep"
                      :placeholder="$t('organization.kep')"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.kep.$error,
                        'is-valid': submitted && !$v.form.kep.$error,
                      }"
                    />
                  </div>
                </div>

                <!---->
              </b-card-body>
            </b-collapse>
          </b-card>

          <b-card no-body class="mb-1 shadow-none">
            <b-card-header header-tag="header" role="tab">
              <!-- toggle adı değiş -->
              <a v-b-toggle="'accordion_contact'" class="text-dark" href="javascript: void(0);">
                <div class="row">
                  <div class="col-md-6">
                    <h5>
                      <i class="bx bx-building-house"></i>
                      {{ $t("organization.contact-information") }}
                    </h5>
                  </div>
                  <div class="col-md-6 text-right">
                    <i v-if="submitted && !$v.form.$anyError" class="bx bx-check text-success h1"></i>
                    <i v-if="submitted && $v.form.$anyError" class="bx bx-x text-danger h1"></i>
                  </div>
                </div>
              </a>
            </b-card-header>
            <b-collapse id="accordion_contact" :visible="false" accordion="accordion" role="tabpanel">
              <b-card-body>
                <!-- -->
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.contact-name") }}</label>
                    <input
                      v-model="form.contactName"
                      class="form-control"
                      :placeholder="$t('organization.contact-name')"
                      :class="{
                        'is-invalid': submitted && $v.form.contactName.$error,
                        'is-valid': submitted && !$v.form.contactName.$error,
                      }"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.contact-email") }}</label>
                    <input
                      v-model="form.contactEmail"
                      type="email"
                      :placeholder="$t('organization.contact-email')"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.contactEmail.$error,
                        'is-valid': submitted && !$v.form.contactEmail.$error,
                      }"
                    />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.contact-phone") }}</label>
                    <input
                      v-model="form.contactPhone"
                      placeholder="+90 xxx xxx xx xx"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.contactPhone.$error,
                        'is-valid': submitted && !$v.form.contactPhone.$error,
                      }"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.contact-adress") }}</label>
                    <input
                      v-model="form.contactAddress"
                      :placeholder="$t('organization.contact-adress')"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.contactAddress.$error,
                        'is-valid': submitted && !$v.form.contactAddress.$error,
                      }"
                    />
                  </div>
                </div>

                <!---->
              </b-card-body>
            </b-collapse>
          </b-card>

          <b-card no-body class="mb-1 shadow-none">
            <b-card-header header-tag="header" role="tab">
              <!-- toggle adı değiş -->
              <a v-b-toggle="'accordion_data_protection'" class="text-dark" href="javascript: void(0);">
                <div class="row">
                  <div class="col-md-6">
                    <h5>
                      <i class="bx bx-building-house"></i>
                      {{ $t("organization.data-protection-information") }}
                    </h5>
                  </div>
                  <div class="col-md-6 text-right">
                    <i v-if="submitted && !$v.form.$anyError" class="bx bx-check text-success h1"></i>
                    <i v-if="submitted && $v.form.$anyError" class="bx bx-x text-danger h1"></i>
                  </div>
                </div>
              </a>
            </b-card-header>
            <b-collapse id="accordion_data_protection" :visible="false" accordion="accordion" role="tabpanel">
              <b-card-body>
                <!-- -->
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.data-protection-name") }}</label>
                    <input
                      v-model="form.dataProtectionName"
                      class="form-control"
                      :placeholder="$t('organization.data-protection-name')"
                      :class="{
                        'is-invalid': submitted && $v.form.dataProtectionName.$error,
                        'is-valid': submitted && !$v.form.dataProtectionName.$error,
                      }"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.data-protection-email") }}</label>
                    <input
                      v-model="form.dataProtectionEmail"
                      :placeholder="$t('organization.data-protection-email')"
                      class="form-control"
                      type="email"
                      :class="{
                        'is-invalid': submitted && $v.form.dataProtectionEmail.$error,
                        'is-valid': submitted && !$v.form.dataProtectionEmail.$error,
                      }"
                    />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.data-protection-phone") }}</label>
                    <input
                      v-model="form.dataProtectionPhone"
                      placeholder="+90 xxx xxx xx xx"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.dataProtectionPhone.$error,
                        'is-valid': submitted && !$v.form.dataProtectionPhone.$error,
                      }"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.data-protection-adress") }}</label>
                    <input
                      v-model="form.dataProtectionAdress"
                      :placeholder="$t('organization.data-protection-adress')"
                      class="form-control"
                      :class="{
                        'is-invalid': submitted && $v.form.dataProtectionAdress.$error,
                        'is-valid': submitted && !$v.form.dataProtectionAdress.$error,
                      }"
                    />
                  </div>
                </div>

                <!---->
              </b-card-body>
            </b-collapse>
          </b-card>
        </div>
      </form>
      <sweet-button @click="save()" type="submit" :loading="isUpdating" :disabled="isUpdating || $v.$invalid" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-2">
        {{ $t("common.save") }}
      </sweet-button>
    </div>
  </form>
</template>

<script>
import ImageCropper from "@c/form/image-cropper";
import { email, maxLength, minLength, required } from "vuelidate/lib/validators";
import Countries from "../countries/countries.json";
import { image as defaultImage } from "./default-organization-image";

const state = "organization";
const schema = {
  logo: defaultImage,
  nid: undefined,
  title: undefined,
  taxOffice: undefined,
  phone: undefined,
  address: undefined,
  web: undefined,
  email: undefined,
  kep: undefined,
  contactName: undefined,
  contactEmail: undefined,
  contactPhone: undefined,
  contactAddress: undefined,
  dataProtectionName: undefined,
  dataProtectionEmail: undefined,
  dataProtectionPhone: undefined,
  dataProtectionAdress: undefined,
};

export default {
  components: { ImageCropper },

  data() {
    return {
      request: {
        options: { page: 1, limit: 10, sort: { name: "asc" } },
        query: {},
      },
      form: { ...schema, logo: defaultImage, country: [] },
      workspace: undefined,
      submitted: false,
    };
  },
  validations: {
    form: {
      country: {
        required,
      },

      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(350),
      },
      nid: {
        minLength: minLength(10),
        maxLength: maxLength(11),
        // minLength: minLength(10), Eski sistemde Veri sorumlusu için kullanılan validasyon VKN/TCKN
        // maxLength: maxLength(11),
      },
      title: {},
      taxOffice: {},
      phone: {
        minLength: minLength(10),
        maxLength: maxLength(17),
        isPhone(value) {
          const regex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
          return value ? regex.test(value) : true;
        },
      },

      email: { email },
      kep: {},
      address: {},
      web: {},
      contactName: {},
      contactEmail: { email },
      contactPhone: {},
      contactAddress: {},
      dataProtectionName: {},
      dataProtectionEmail: { email },
      dataProtectionPhone: {},
      dataProtectionAdress: {},
    },
  },
  computed: {
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
    editedOrganization() {
      return this.$store.getters[`${state}/editedOrganization`];
    },
    isEditMode() {
      return this.$store.getters[`${state}/isEditMode`];
    },
    workspaces() {
      return this.$store.getters["workspace/pagination"];
    },

    pagination() {
      return this.$store.getters["organization/pagination"];
    },
    isFormValid() {
      if (!this.isEditMode && !this.workspace) {
        return false;
      }
      return !this.$v.$invalid;
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.$v.form.$touch();
      },
    },
    editedOrganization: {
      deep: true,
      immediate: true,
      handler(v) {
        if (v) {
          this.form = { ...v };
        } else {
          this.resetForm();
        }
        if (!this.form.logo) {
          this.form.logo = defaultImage;
        }
      },
    },
    request: {
      deep: true,
      immediate: true,
      handler() {
        this.debounceMixin(() => this.paginate());
      },
    },
  },
  async mounted() {
    await this.$store.dispatch("workspace/paginate");

    const selectedWorkspace = this.$store.getters["switchWorkspace/selected"];
    if (selectedWorkspace) {
      this.workspace = selectedWorkspace;
    }
  },
  methods: {
    async save() {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }

      try {
        await this.$store.dispatch(`${state}/save`, {
          organization: this.form,
          workspace: this.workspace,
        });

        this.resetForm();
        this.$toast.success(this.$t("common.success"));
        this.$root.$emit("bv::toggle::collapse", "sidebar-new-organization");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    setWorkspace(workspace) {
      this.workspace = workspace;
    },
    openCropper() {
      this.$bvModal.show("imageCropper");
    },
    cropperImage(value) {
      this.form.logo = value;
      this.$bvModal.hide("imageCropper");
      this.$forceUpdate();
    },
    resetForm() {
      this.form = { ...schema };
    },
    async onCountriesSearch(value, callback) {
      const lang = "tr";
      const response = Countries.filter((x) => x.name[lang].toLowerCase().includes(value.toLowerCase()));
      callback(response);
    },
    onCountriesSelect(item) {
      this.form = { ...this.form, country: item };
    },

    paginate() {
      this.$store.dispatch(`workspace/paginate`, { pagination: this.request, showOnlyIfRequestedAccountisAdmin: true });
    },
  },
};
</script>
